// Builds one frame's draw list: the shot's picture (or this moment's frame of its animation), then each character's
// talking face on top. Every value comes from saved data and the time, so preview and export always match. The
// renderer (src/render/FrameSvg.tsx) only draws the list.
import { faceSet, sideMouthMirrored, faceSpritesInUse, faceStyleOf, type FacePart } from "../library/faceSets";
import { shotMotion, type Camera, type Line, type Picture, type Shot, type ShotFace } from "../model/episode";
import type { Emotion, FaceSpots, Spot, Viseme } from "../model/face";
import type { Library } from "../model/library";
import { FRAME_H, FRAME_W, WHOLE, pictureFit, viewRect, type View } from "./camera";
import { emotionAt, isBlinking } from "./face";
import { motionFaces, motionIndex } from "./motion";
import { FPS, segAt, type Timeline } from "./timeline";

export type DrawImage = { href: string; x: number; y: number; w: number; h: number; rot?: number; flip?: boolean }; // rot: degrees clockwise about its centre; flip: mirrored left to right
type Boil = { strength: number; every: number };
export type DrawFrame = { frame: number; view: View; base: string; boil: Boil | null; images: DrawImage[] };

export const BASE = "#2B2E33";
export const DEFAULT_WIGGLE = 3.2;

type FrameTime = {
  t: number; // seconds into the line: feelings and the mouth
  clock: number; // seconds of wall time: blinks and the wiggle keep going while a frame is held
  visemes?: Viseme[]; // the speaker's mouth, one per frame; none = mouths at rest
  mouth?: Viseme; // or one fixed mouth for the speaker (previews)
  blink?: boolean; // force eyes open or shut instead of the blink schedule (previews)
  shotT?: number; // seconds since the shot's first line started: a moving picture plays from there
  emotion?: Emotion; // override the speaker's feeling (previews)
  motionFrame?: number; // show this frame of a moving picture (editors)
};

type Fit = ReturnType<typeof pictureFit>;

function spritePart(part: FacePart, spot: Spot, fit: Fit, tilt = 0, flip = false): DrawImage {
  const k = (spot.w / part.ref.w) * fit.s;
  const w = part.sprite.w * k, h = part.sprite.h * k;
  return { href: part.sprite.src, x: fit.x + spot.x * fit.s - w / 2, y: fit.y + spot.y * fit.s - h / 2, w, h, ...(tilt ? { rot: tilt } : {}), ...(flip ? { flip } : {}) };
}

// The eyes, brows and mouth for a face at `spots` on a picture placed by `fit`. Each part stays exactly where it was
// placed; only which sprite is drawn changes (feelings, blinks, mouth shapes).
// `style`: the character's face style (none: the default). A face turned left or right uses the set's 45° sprites,
// which look right, mirrored for left.
export function faceImages(spots: FaceSpots, fit: Fit, emotion: Emotion, blink: boolean, mouth: Viseme = "X", style?: string): DrawImage[] {
  const set = faceSet(style, spots.turn ? "side" : "front"), t = spots.tilt ?? 0, flip = spots.turn === "left";
  return [
    spritePart(set.eyes(emotion, blink), spots.eyes, fit, t, flip),
    spritePart(set.brows(emotion), spots.brows, fit, t, flip),
    spritePart(set.mouth(mouth, emotion), spots.mouth, fit, t, spots.turn ? flip !== sideMouthMirrored(style) : false),
  ];
}

// A picture on its own, with a face on it if given (library previews).
export function pictureFrame(pic: Picture, spots: FaceSpots | undefined, at: Pick<FrameTime, "clock" | "mouth" | "blink" | "emotion">, view?: View, style?: string): DrawFrame {
  const fit = pictureFit(pic);
  const images: DrawImage[] = [{ href: pic.src, x: fit.x, y: fit.y, w: pic.w * fit.s, h: pic.h * fit.s }];
  if (spots) images.push(...faceImages(spots, fit, at.emotion ?? "neutral", at.blink ?? isBlinking(null, "", 0, at.clock), at.mouth, style));
  return { frame: Math.floor(at.clock * FPS), view: view ?? viewRect(WHOLE), base: "transparent", boil: null, images };
}

// A shot during one of its lines (or a still of the shot, when `line` is null), seen through `camera` (default: the
// line's camera, or the whole picture).
export function shotFrame(shot: Shot, line: Line | null, at: FrameTime, camera?: Camera): DrawFrame {
  const frame = Math.floor(at.clock * FPS);
  const view = viewRect(camera ?? line?.camera ?? WHOLE);
  if (!shot.picture) return { frame, view, base: BASE, boil: null, images: [] };
  // A moving shot shows the frame for the time since the shot started; the still is frame 0.
  const m = shotMotion(shot);
  const mi = m ? at.motionFrame ?? (at.shotT != null ? motionIndex(m, at.shotT) : 0) : 0;
  const pic = m?.frames[mi] ?? shot.picture;
  const faces = m && mi > 0 ? motionFaces(shot, m)[mi] ?? shot.faces : shot.faces;
  const fit = pictureFit(pic);
  const images: DrawImage[] = [{ href: pic.src, x: fit.x, y: fit.y, w: pic.w * fit.s, h: pic.h * fit.s }];
  const speaker = line?.speaker;
  const speakerFace = faces.find(f => f.character === speaker);
  for (const face of faces) {
    const isSpeaker = face === speakerFace;
    const emotion = isSpeaker && at.emotion ? at.emotion : emotionAt(line, face.character, at.t);
    const blink = at.blink ?? isBlinking(line, face.character, at.t, at.clock);
    const mouth = isSpeaker ? at.mouth ?? at.visemes?.[Math.floor(at.t * FPS)] : undefined;
    images.push(...faceImages(face, fit, emotion, blink, mouth, faceStyleOf(face.character)));
  }
  return { frame, view, base: BASE, boil: shot.wiggle > 0 ? { strength: shot.wiggle, every: 3 } : null, images };
}

export const emptyFrame = (clock = 0): DrawFrame => ({ frame: Math.floor(clock * FPS), view: viewRect(WHOLE), base: BASE, boil: null, images: [] });

// The episode at time t.
export function frameAt(tl: Timeline, t: number): DrawFrame {
  const seg = segAt(tl, t);
  if (!seg) return emptyFrame(t);
  const local = Math.max(0, t - seg.start);
  return shotFrame(seg.shot, seg.line, { t: local, clock: t, shotT: t - seg.shotStart, visemes: local < seg.end - seg.start ? seg.visemes : undefined });
}

// Every image the episode can show between two times: each shot's picture, every frame of its animation, and the face sprites.
export function imagesIn(tl: Timeline, from: number, to: number): string[] {
  const out = faceSpritesInUse();
  for (const { shot, start, next } of tl.segs) {
    if (next <= from || start >= to) continue;
    const m = shotMotion(shot);
    if (shot.picture) out.push(shot.picture.src);
    if (m) out.push(...m.frames.map(f => f.src));
  }
  return out;
}

// A face's middle in the frame, between the eyes and the mouth.
function faceCentre(shot: Shot, face: ShotFace) {
  if (!shot.picture) return { x: FRAME_W / 2, y: FRAME_H / 2 };
  const f = pictureFit(shot.picture);
  return { x: f.x + ((face.eyes.x + face.mouth.x) / 2) * f.s, y: f.y + ((face.eyes.y + face.mouth.y) / 2) * f.s };
}

// The camera choices for a line: the whole picture, then a mid shot and a close-up on each face.
export function cameraChoices(shot: Shot, lib: Library): { key: string; label: string; camera: Camera }[] {
  const out = [{ key: "whole", label: "Whole picture", camera: WHOLE }];
  for (const face of shot.faces) {
    const c = faceCentre(shot, face);
    const name = lib.assets.find(a => a.id === face.character)?.name ?? "?";
    // Zoom from how big the face is: a close-up makes the face about a third of the frame's width.
    const fw = shot.picture ? face.eyes.w * pictureFit(shot.picture).s * 1.8 : FRAME_W / 6;
    const close = Math.min(6, Math.max(1.4, FRAME_W / 3 / fw));
    const mid = Math.max(1.2, close * 0.55);
    // The face sits a little above the middle of the frame.
    const at = (zoom: number): Camera => ({ cx: c.x / FRAME_W, cy: (c.y + (FRAME_H / zoom) * 0.12) / FRAME_H, zoom });
    out.push({ key: `mid-${face.character}`, label: `${name}, mid`, camera: at(mid) });
    out.push({ key: `close-${face.character}`, label: `${name}, close`, camera: at(close) });
  }
  return out;
}
